/**
 * Get color for lifecycle stage
 * Single Responsibility: Color mapping logic
 */
export const getStageColor = (stage: string): string => {
  const colors: Record<string, string> = {
    ingredientImpact: "#10b981",
    packagingImpact: "#f59e0b",
    transportImpact: "#3b82f6",
    processingImpact: "#047857"
  };
  return colors[stage] || "#6b7280";
};

/**
 * Get tooltip text for lifecycle stage
 * Single Responsibility: Tooltip content logic
 */
export const getStageTooltip = (stage: string): string => {
  const tooltips: Record<string, string> = {
    packagingImpact:
      "Includes production of packaging materials and associated processing. Examples include bottles, cartons, plastics, and related energy use.",
    transportImpact:
      "Includes transportation between supply chain stages. This typically covers raw materials to your facility and distribution of finished products.Includes transportation between supply chain stages. This typically covers raw materials to your facility and distribution of finished products.",
    processingImpact:
      "Includes all operations within your facility. This covers mapped production steps (e.g. washing, pasteurization, drying), as well as energy, water, chemicals, and production losses. Chemical impacts include both their production and use.",
    ingredientImpact:
      "This includes the impact from the ingredients themselves. Some may be raw (like crops), others may already be processed."
  };
  return tooltips[stage] || "Lifecycle stage impact";
};

export const getStageSubtitle = (stage: string): string => {
  const subtitles: Record<string, string> = {
    packagingImpact: "Movement between supply chain stages",
    transportImpact: "Packaging materials and related processing",
    processingImpact: "All operations inside your facility (machines, energy, water, chemicals)",
    ingredientImpact: "Ingredients used in the product (raw or processed before production)"
  };
  return subtitles[stage] || stage.replace(/([a-z])([A-Z])/g, "$1 $2");
};

/**
 * Format date from ISO string
 * Single Responsibility: Date formatting logic
 */
export const formatCalculatedDate = (createdAt?: string): string => {
  if (!createdAt) return "Not calculated";

  const d = new Date(createdAt);
  if (isNaN(d.getTime())) return "Not calculated";

  return d.toISOString().slice(0, 19).replace("T", " ");
};

/**
 * Process lifecycle stages with cultivation impact added to ingredient impact
 * Single Responsibility: Lifecycle stage transformation logic
 */
export const processLifecycleStages = (
  cycleStageData: any,
  NOT_APPLICABLE: string,
  humanize: (str: string) => string
) => {
  // Extract cultivation impact
  const cultivationImpactRaw =
    cycleStageData.cultivationImpact || cycleStageData.cultivation_impact || 0;
  const cultivationImpactValue =
    typeof cultivationImpactRaw === "string"
      ? parseFloat(cultivationImpactRaw)
      : Number(cultivationImpactRaw) || 0;

  // Get all stages except cultivation
  const stages = Object.entries(cycleStageData).filter(([key]) => {
    const normalizedKey = key.toLowerCase().replace(/_/g, "");
    return normalizedKey !== "cultivationimpact";
  });

  return stages.map(([key, value]) => {
    const normalizedKey = key.toLowerCase().replace(/_/g, "");
    let pctValue = value === NOT_APPLICABLE ? 0 : parseFloat(value as string) || 0;

    // Add cultivation to ingredient impact
    if (normalizedKey === "ingredientimpact") {
      pctValue = pctValue + cultivationImpactValue;
    }

    return {
      label: humanize(key),
      subtitle: getStageSubtitle(key),
      pct: pctValue,
      color: getStageColor(key),
      tooltip: getStageTooltip(key),
      drilldown: []
    };
  });
};
